import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import type { LocationPoint } from "./build-charts";

const HEAT_RADIUS = 26;
const HEAT_BLUR = 18;

/** Build a 256-color RGBA lookup table for the heatmap color ramp. */
function buildGradientLut(): Uint8ClampedArray {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 256;
  const ctx = canvas.getContext("2d");
  if (!ctx) return new Uint8ClampedArray(256 * 4);

  const grad = ctx.createLinearGradient(0, 0, 0, 256);
  grad.addColorStop(0.15, "#3b82f6");
  grad.addColorStop(0.4, "#06b6d4");
  grad.addColorStop(0.65, "#10b981");
  grad.addColorStop(0.85, "#f59e0b");
  grad.addColorStop(1.0, "#ef4444");

  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1, 256);
  return ctx.getImageData(0, 0, 1, 256).data;
}

/** Build a reusable radial alpha stamp sprite for a single heat point. */
function buildStampCanvas(radius: number, blur: number): HTMLCanvasElement {
  const r = radius + blur;
  const stamp = document.createElement("canvas");
  stamp.width = r * 2;
  stamp.height = r * 2;
  const ctx = stamp.getContext("2d");
  if (!ctx) return stamp;

  const grad = ctx.createRadialGradient(r, r, radius * 0.15, r, r, r);
  grad.addColorStop(0, "rgba(0,0,0,1)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, r * 2, r * 2);
  return stamp;
}

function drawHeatmap(
  canvas: HTMLCanvasElement,
  map: L.Map,
  points: LocationPoint[],
  lut: Uint8ClampedArray,
  stamp: HTMLCanvasElement,
) {
  const size = map.getSize();
  const width = Math.max(1, size.x);
  const height = Math.max(1, size.y);
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return;
  ctx.clearRect(0, 0, width, height);

  if (points.length === 0) return;

  const r = HEAT_RADIUS + HEAT_BLUR;
  const cellSize = Math.max(4, Math.floor(HEAT_RADIUS / 2));
  const grid = new Map<string, { x: number; y: number; count: number }>();

  let maxCount = 1;
  for (const pt of points) {
    const px = map.latLngToContainerPoint([pt.lat, pt.lng]);
    if (px.x < -r || px.y < -r || px.x > width + r || px.y > height + r) continue;
    const gx = Math.floor(px.x / cellSize);
    const gy = Math.floor(px.y / cellSize);
    const key = `${gx}:${gy}`;
    const cell = grid.get(key);
    if (cell) {
      cell.x = (cell.x * cell.count + px.x) / (cell.count + 1);
      cell.y = (cell.y * cell.count + px.y) / (cell.count + 1);
      cell.count += 1;
      if (cell.count > maxCount) maxCount = cell.count;
    } else {
      grid.set(key, { x: px.x, y: px.y, count: 1 });
    }
  }

  if (grid.size === 0) return;

  // Soft logarithmic scaling so both isolated points and dense clusters remain visible
  const denom = Math.max(1.5, Math.log1p(maxCount));
  for (const cell of grid.values()) {
    const alpha = Math.min(1, Math.max(0.28, Math.log1p(cell.count) / denom));
    ctx.globalAlpha = alpha;
    ctx.drawImage(stamp, cell.x - r, cell.y - r);
  }

  const img = ctx.getImageData(0, 0, width, height);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0) continue;
    const offset = a * 4;
    data[i] = lut[offset];
    data[i + 1] = lut[offset + 1];
    data[i + 2] = lut[offset + 2];
    data[i + 3] = Math.min(215, Math.round(a * 0.85));
  }
  ctx.putImageData(img, 0, 0);
}

interface LocationHeatmapCardProps {
  title: string;
  points: LocationPoint[];
}

export function LocationHeatmapCard({ title, points }: LocationHeatmapCardProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const pathLayerRef = useRef<L.LayerGroup | null>(null);
  const lutRef = useRef<Uint8ClampedArray | null>(null);
  const stampRef = useRef<HTMLCanvasElement | null>(null);

  const [showHeat, setShowHeat] = useState(true);
  const [showPoints, setShowPoints] = useState(true);
  const [showPath, setShowPath] = useState(false);

  // Initialize Leaflet map once
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || mapRef.current) return;

    lutRef.current = buildGradientLut();
    stampRef.current = buildStampCanvas(HEAT_RADIUS, HEAT_BLUR);

    const map = L.map(container, {
      center: [33.4242, -111.9281],
      zoom: 12,
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);
    pathLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      markersLayerRef.current = null;
      pathLayerRef.current = null;
    };
  }, []);

  // Fit bounds when points change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || points.length === 0) return;

    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 16 });
  }, [points]);

  // Redraw heatmap canvas on map move/zoom or toggle change
  useEffect(() => {
    const map = mapRef.current;
    const canvas = canvasRef.current;
    const lut = lutRef.current;
    const stamp = stampRef.current;
    if (!map || !canvas || !lut || !stamp) return;

    const render = () => {
      if (!showHeat) {
        const ctx = canvas.getContext("2d");
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }
      drawHeatmap(canvas, map, points, lut, stamp);
    };

    render();
    map.on("move zoom moveend zoomend resize", render);
    return () => {
      map.off("move zoom moveend zoomend resize", render);
    };
  }, [points, showHeat]);

  // Update interactive point markers & trajectory polyline
  useEffect(() => {
    const markersLayer = markersLayerRef.current;
    const pathLayer = pathLayerRef.current;
    if (!markersLayer || !pathLayer) return;

    markersLayer.clearLayers();
    pathLayer.clearLayers();

    if (showPath && points.length >= 2) {
      const latLngs = points.map((p) => [p.lat, p.lng] as [number, number]);
      L.polyline(latLngs, {
        color: "#2563EB",
        weight: 2,
        opacity: 0.65,
        dashArray: "4 4",
      }).addTo(pathLayer);
    }

    if (showPoints) {
      for (const p of points) {
        const marker = L.circleMarker([p.lat, p.lng], {
          radius: 4,
          color: "#1E293B",
          weight: 1,
          fillColor: "#2563EB",
          fillOpacity: 0.85,
        });
        const details = [
          `<strong>${p.label} UTC</strong>`,
          `${p.lat.toFixed(5)}°, ${p.lng.toFixed(5)}°`,
          p.accuracy !== null ? `Accuracy: ±${Math.round(p.accuracy)} m` : null,
          p.timeOfDay ? `Time of day: ${p.timeOfDay}` : null,
        ]
          .filter(Boolean)
          .join("<br/>");
        marker.bindPopup(details);
        marker.addTo(markersLayer);
      }
    }
  }, [points, showPoints, showPath]);

  const handleFitBounds = () => {
    const map = mapRef.current;
    if (!map || points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 16 });
  };

  return (
    <div className="rounded-md border bg-white p-4 shadow-xs">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-[#1E293B]">{title}</h3>
          <p className="text-xs text-muted-foreground">
            {points.length.toLocaleString()} GPS fix{points.length === 1 ? "" : "es"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant={showHeat ? "default" : "outline"}
            className="h-7 px-2.5 text-xs"
            onClick={() => setShowHeat((v) => !v)}
          >
            Heatmap
          </Button>
          <Button
            type="button"
            size="sm"
            variant={showPoints ? "default" : "outline"}
            className="h-7 px-2.5 text-xs"
            onClick={() => setShowPoints((v) => !v)}
          >
            Points
          </Button>
          <Button
            type="button"
            size="sm"
            variant={showPath ? "default" : "outline"}
            className="h-7 px-2.5 text-xs"
            onClick={() => setShowPath((v) => !v)}
          >
            Path
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={handleFitBounds}
          >
            Reset view
          </Button>
        </div>
      </div>

      <div className="relative h-[300px] w-full overflow-hidden rounded border">
        <div ref={mapContainerRef} className="h-full w-full z-0" />
        <canvas
          ref={canvasRef}
          className="pointer-events-none absolute inset-0 z-[400]"
        />
      </div>

      {showHeat && (
        <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Low density</span>
            <div
              className="h-2 w-28 rounded-full"
              style={{
                background:
                  "linear-gradient(to right, #3b82f6, #06b6d4, #10b981, #f59e0b, #ef4444)",
              }}
            />
            <span>High density</span>
          </div>
          <span>Click any point for fix details</span>
        </div>
      )}
    </div>
  );
}
